import { apiClient } from '@/shared/lib/axios';
import { downloadBlob, parseFilenameFromHeader } from '@/utils/blobDownload';
import type {
  SyncNotification,
  SyncNotificationListMeta,
  SyncNotificationQuery,
  SyncNotificationSiteScope,
} from '@/types/admin-sync-notification';

export interface SyncNotificationListResponse {
  success: boolean;
  message: string;
  data: {
    data: SyncNotification[];
    meta: SyncNotificationListMeta;
    site_scope: SyncNotificationSiteScope;
  };
}

export interface SyncNotificationCountResponse {
  success: boolean;
  message: string;
  data: {
    /** BD 1.0 — total unread across every listed category, within this admin's site scope. */
    count: number;
    /** Unread per inbox, every listed category present (zeros included) — feeds the tab badges. */
    by_category: Record<string, number>;
    /** BD 17.0 — records 全て再同期 would act on, per inbox. Enables the button. */
    resync_pending: Record<string, number>;
  };
}

export interface MarkReadSyncNotificationResponse {
  success: boolean;
  message: string;
  data: {
    read: boolean;
  };
}

export interface RetrySyncResponse {
  success: boolean;
  message: string;
  data: {
    records: number;
    from_failed: number;
  };
}

/** BD 17.0 全て再同期 — the whole inbox, every page, every aggregate. */
export interface RetryAllResponse {
  success: boolean;
  message: string;
  data: {
    /** Records the press picked up. */
    candidates: number;
    /** Of those, how many had come good in the meantime — the 〇件 in BD 17.0's toast. */
    excluded: number;
    /** Actually re-pushed. */
    dispatched: number;
    /** Left over past this press's cap — tells the operator to press again rather than wonder. */
    remaining: number;
    limit: number;
  };
}

export const getSyncNotificationList = async (
  siteType: string | null,
  query?: SyncNotificationQuery
): Promise<SyncNotificationListResponse> => {
  const response = await apiClient.get<SyncNotificationListResponse>(
    `/admin/${siteType}/sync-notifications`,
    { params: query }
  );
  return response.data;
};

export const getSyncNotificationCount = async (
  siteType: string | null
): Promise<SyncNotificationCountResponse> => {
  const response = await apiClient.get<SyncNotificationCountResponse>(
    `/admin/${siteType}/sync-notifications/count`
  );
  return response.data;
};

export const markReadSyncNotification = async (
  siteType: string,
  id: number
): Promise<MarkReadSyncNotificationResponse> => {
  const response = await apiClient.post<MarkReadSyncNotificationResponse>(
    `/admin/${siteType}/sync-notifications/${id}/read`
  );
  return response.data;
};

/**
 * BD 17.0 全て再同期 — re-push everything still open in one inbox.
 *
 * ONE CALL, not one per aggregate: the screen cannot name the aggregates sitting on pages it has not drawn,
 * so 「全て」 has to be resolved server-side or it silently means 「everything on this page」.
 */
export const retrySyncAll = async (
  siteType: string,
  category: string
): Promise<RetryAllResponse> => {
  const response = await apiClient.post<RetryAllResponse>(
    `/admin/${siteType}/sync-notifications/retry-all`,
    null,
    { params: { category } }
  );
  return response.data;
};

/** 全て再同期 (legacy, one aggregate): re-push ALL failed records of an aggregate. */
export const retrySyncAggregate = async (
  siteType: string,
  aggregate: string
): Promise<RetrySyncResponse> => {
  const response = await apiClient.post<RetrySyncResponse>(
    `/admin/${siteType}/sync-notifications/retry/${aggregate}`
  );
  return response.data;
};

/** 再同期 (per-record): re-push ONE record by aggregate_id. */
export const retrySyncRecord = async (
  siteType: string,
  aggregate: string,
  aggregateId: string
): Promise<RetrySyncResponse> => {
  const response = await apiClient.post<RetrySyncResponse>(
    `/admin/${siteType}/sync-notifications/retry/${aggregate}/${aggregateId}`
  );
  return response.data;
};

/**
 * 不一致明細CSV for one 状態の不一致 notice.
 *
 * STREAMED FROM THE API rather than built here, unlike the 通知センター error file: the rows behind a drift
 * notice are not on the screen — the notice carries a count, and the detail was written to disk by the
 * nightly reconcile that produced it. Reusing that endpoint's download helper keeps the two files opening
 * the same way for whoever presses either button.
 */
export const downloadDriftCsv = async (siteType: string, id: number): Promise<void> => {
  const response = await apiClient.get<Blob>(
    `/admin/${siteType}/sync-notifications/${id}/drift-csv`,
    { responseType: 'blob' }
  );

  // THE SERVER'S NAME WINS. It is the BD one — 不一致明細_{治療院ID}_{照合実行日}.csv — and this call used to
  // discard the header outright and always save `reconcile_drift_{id}.csv`, so the same file downloaded from
  // this screen and from 通知センター arrived under two different names. `reconcile_drift_{id}.csv` stays as
  // the fallback the BD names for the case where the browser receives no name from the server.
  downloadBlob(
    response.data,
    parseFilenameFromHeader(response.headers?.['content-disposition']) ??
      `reconcile_drift_${id}.csv`
  );
};
